import { useState, useEffect } from 'react';
import apiClient from '../api/client';
import './Page.css';
import './SettingsPage.css';

const BILLING_CC   = 1;
const BILLING_ACH  = 2;
const BILLING_STMT = 3;

function isInstalled() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}
function isIosSafari() {
  const ua = window.navigator.userAgent;
  return /iphone|ipad|ipod/i.test(ua) && /safari/i.test(ua) && !/crios|fxios|opios/i.test(ua);
}

function getMemberId(): number | null {
  try { return JSON.parse(sessionStorage.getItem('member') ?? '{}').personId ?? null; }
  catch { return null; }
}

// ── Chevron icon ──────────────────────────────────────────────────────────
function Chevron() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={16} height={16}>
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

// ── Bottom sheet wrapper ───────────────────────────────────────────────────
function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <>
      <div className="settings-overlay" onClick={onClose} />
      <div className="settings-sheet">
        <div className="settings-sheet-handle" />
        <div className="settings-sheet-header">
          <h2 className="settings-sheet-title">{title}</h2>
          <button className="settings-sheet-cancel" type="button" onClick={onClose}>Exit</button>
        </div>
        {children}
      </div>
    </>
  );
}

// ── Change Password form ───────────────────────────────────────────────────
function PasswordSheet({ onClose }: { onClose: () => void }) {
  const [current, setCurrent]   = useState('');
  const [next, setNext]         = useState('');
  const [confirm, setConfirm]   = useState('');
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [loading, setLoading]   = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(''); setSuccess('');
    if (next !== confirm) { setError('New passwords do not match.'); return; }
    setLoading(true);
    try {
      const personId = getMemberId();
      await apiClient.post('/Authentication/ChangePassword', {
        personId, currentPassword: current, newPassword: next, confirmPassword: next,
      });
      setSuccess('Password changed successfully.');
      setCurrent(''); setNext(''); setConfirm('');
    } catch {
      setError('Failed to change password. Check your current password and try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Sheet title="Change Password" onClose={onClose}>
      <form onSubmit={handleSubmit} className="settings-form">
        <label className="settings-label">
          Current Password
          <input className="settings-input" type="password" autoComplete="current-password"
            autoCapitalize="none" autoCorrect="off"
            value={current} onChange={e => setCurrent(e.target.value)} required />
        </label>
        <label className="settings-label">
          New Password
          <input className="settings-input" type="password" autoComplete="new-password"
            autoCapitalize="none" autoCorrect="off"
            value={next} onChange={e => setNext(e.target.value)} required />
        </label>
        <label className="settings-label">
          Confirm New Password
          <input className="settings-input" type="password" autoComplete="new-password"
            autoCapitalize="none" autoCorrect="off"
            value={confirm} onChange={e => setConfirm(e.target.value)} required />
        </label>
        {error   && <p className="settings-error">{error}</p>}
        {success && <p className="settings-success">{success}</p>}
        <button className="settings-btn" type="submit" disabled={loading}>
          {loading ? 'Saving…' : 'Update Password'}
        </button>
      </form>
    </Sheet>
  );
}

// ── Change Payment Method form ─────────────────────────────────────────────
interface PaymentInfo {
  billingMethodId: number;
  maskedCCNumber: string | null;
  ccExpirationMonth: string | null;
  ccExpirationYear: number | null;
  maskedRoutingNumber: string | null;
  maskedAccountNumber: string | null;
}

function isMasked(v: string) { return /^\*+\d+$/.test(v); }

function PaymentSheet({ onClose }: { onClose: () => void }) {
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [current, setCurrent]       = useState<PaymentInfo | null>(null);

  const [methodId, setMethodId]     = useState<number>(BILLING_CC);
  const [ccNumber, setCcNumber]     = useState('');
  const [expiry, setExpiry]         = useState('');
  const [routing, setRouting]       = useState('');
  const [accountNum, setAccountNum] = useState('');

  function handleExpiryChange(raw: string) {
    let digits = raw.replace(/\D/g, '').slice(0, 4);
    if (digits.length >= 1 && parseInt(digits[0]) > 1) digits = '0' + digits.slice(0, 3);
    if (digits.length >= 2) {
      const m = parseInt(digits.slice(0, 2));
      if (m < 1)  digits = '01' + digits.slice(2);
      if (m > 12) digits = '12' + digits.slice(2);
    }
    setExpiry(digits);
  }

  useEffect(() => {
    const personId = getMemberId();
    if (!personId) { setLoading(false); return; }
    apiClient.get(`/PwaPayment/${personId}`)
      .then(res => {
        const d: PaymentInfo = res.data?.data;
        if (d) {
          setCurrent(d);
          setMethodId(d.billingMethodId || BILLING_CC);
          if (d.billingMethodId === BILLING_CC) {
            setCcNumber(d.maskedCCNumber ?? '');
            if (d.ccExpirationMonth && d.ccExpirationYear) {
              setExpiry(`${d.ccExpirationMonth}${String(d.ccExpirationYear).slice(-2)}`);
            }
          } else if (d.billingMethodId === BILLING_ACH) {
            setRouting(d.maskedRoutingNumber ?? '');
            setAccountNum(d.maskedAccountNumber ?? '');
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function handleMethodChange(id: number) {
    if (id === BILLING_STMT) return;
    setMethodId(id);
    setError('');
    if (id === BILLING_CC) {
      setCcNumber('');
      setExpiry('');
    } else {
      setRouting('');
      setAccountNum('');
    }
  }

  function handleCcFocus() {
    if (isMasked(ccNumber)) setCcNumber('');
  }
  function handleCcBlur() {
    if (!ccNumber && current?.maskedCCNumber) setCcNumber(current.maskedCCNumber);
  }
  function handleRoutingFocus() {
    if (isMasked(routing)) setRouting('');
  }
  function handleRoutingBlur() {
    if (!routing && current?.maskedRoutingNumber) setRouting(current.maskedRoutingNumber);
  }
  function handleAccountFocus() {
    if (isMasked(accountNum)) setAccountNum('');
  }
  function handleAccountBlur() {
    if (!accountNum && current?.maskedAccountNumber) setAccountNum(current.maskedAccountNumber);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(''); setSuccess('');

    if (methodId === BILLING_CC) {
      if (!isMasked(ccNumber)) {
        const digits = ccNumber.replace(/\D/g, '');
        if (digits.length !== 16)
          return setError('Card number must be 16 digits.');
      }
      if (!/^\d{4}$/.test(expiry)) return setError('Expiry must be in MMYY format (e.g. 0827).');
      const month = parseInt(expiry.slice(0, 2));
      const year  = 2000 + parseInt(expiry.slice(2));
      if (month < 1 || month > 12) return setError('Invalid expiry month.');
      const now = new Date();
      if (year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1))
        return setError('Card is expired.');
    } else {
      if (!isMasked(routing)) {
        const digits = routing.replace(/\D/g, '');
        if (digits.length !== 9) return setError('Routing number must be 9 digits.');
      }
      if (!isMasked(accountNum)) {
        const digits = accountNum.replace(/\D/g, '');
        if (digits.length < 4 || digits.length > 17) return setError('Account number must be 4–17 digits.');
      }
    }

    const personId = getMemberId();
    if (!personId) return setError('Session error. Please log in again.');

    setSaving(true);
    try {
      const payload: Record<string, unknown> = { billingMethodId: methodId };
      if (methodId === BILLING_CC) {
        if (!isMasked(ccNumber)) payload.ccNumber = ccNumber.replace(/\D/g, '');
        payload.expiryMMYY = expiry;
      } else {
        if (!isMasked(routing))    payload.achRoutingNumber = routing.replace(/\D/g, '');
        if (!isMasked(accountNum)) payload.achAccountNumber = accountNum.replace(/\D/g, '');
      }
      await apiClient.post(`/PwaPayment/${personId}`, payload);
      setSuccess('Payment method updated successfully.');
    } catch {
      setError('Failed to update payment method. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet title="Update Payment Method" onClose={onClose}>
      {loading ? (
        <p className="settings-loading">Loading…</p>
      ) : (
        <form onSubmit={handleSubmit} className="settings-form">
          <label className="settings-label">
            Bill Method
            <div className="settings-select-wrap">
              <select
                className="settings-select"
                value={methodId}
                onChange={e => handleMethodChange(Number(e.target.value))}
              >
                <option value={BILLING_CC}>Credit Card</option>
                <option value={BILLING_ACH}>Bank Draft</option>
                <option value={BILLING_STMT} disabled>Statement</option>
              </select>
              <Chevron />
            </div>
          </label>

          {methodId === BILLING_CC && (
            <>
              <label className="settings-label">
                CC #
                <input
                  className="settings-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-number"
                  autoCapitalize="none"
                  autoCorrect="off"
                  placeholder="Card number"
                  value={ccNumber}
                  onChange={e => setCcNumber(e.target.value.replace(/\D/g, '').slice(0, 16))}
                  onFocus={handleCcFocus}
                  onBlur={handleCcBlur}
                />
              </label>
              <label className="settings-label">
                Expiration Date
                <input
                  className="settings-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-exp"
                  autoCapitalize="none"
                  autoCorrect="off"
                  placeholder="MMYY"
                  maxLength={4}
                  value={expiry}
                  onChange={e => handleExpiryChange(e.target.value)}
                />
              </label>
            </>
          )}

          {methodId === BILLING_ACH && (
            <>
              <label className="settings-label">
                Routing Number
                <input
                  className="settings-input"
                  type="text"
                  inputMode="numeric"
                  autoCapitalize="none"
                  autoCorrect="off"
                  placeholder="9-digit routing number"
                  maxLength={9}
                  value={routing}
                  onChange={e => setRouting(e.target.value.replace(/\D/g, ''))}
                  onFocus={handleRoutingFocus}
                  onBlur={handleRoutingBlur}
                />
              </label>
              <label className="settings-label">
                Account Number
                <input
                  className="settings-input"
                  type="text"
                  inputMode="numeric"
                  autoCapitalize="none"
                  autoCorrect="off"
                  placeholder="Account number"
                  value={accountNum}
                  onChange={e => setAccountNum(e.target.value)}
                  onFocus={handleAccountFocus}
                  onBlur={handleAccountBlur}
                />
              </label>
            </>
          )}

          {error   && <p className="settings-error">{error}</p>}
          {success && <p className="settings-success">{success}</p>}
          <button className="settings-btn" type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </form>
      )}
    </Sheet>
  );
}

// ── My Plan sheet ─────────────────────────────────────────────────────────
interface PlanInfo {
  packageName: string;
  membershipStatus: string;
  membershipBegins: string | null;
  nextBillDate: string | null;
  billingAmount: number | null;
  lastCheckIn: string | null;
  checkInsThisWeek: number;
}

function fmtPlanDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function PlanSheet({ onClose }: { onClose: () => void }) {
  const [plan, setPlan] = useState<PlanInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const personId = getMemberId();
    if (!personId) { setLoading(false); return; }
    apiClient.get(`/PwaDashboard/${personId}`)
      .then(res => setPlan(res.data?.data ?? null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <Sheet title="My Plan" onClose={onClose}>
      {loading ? (
        <p className="settings-loading">Loading…</p>
      ) : !plan || !plan.packageName ? (
        <p className="settings-loading">No plan information available.</p>
      ) : (
        <div className="plan-rows">
          <div className="plan-row">
            <span className="plan-row__label">Package</span>
            <span className="plan-row__value">{plan.packageName}</span>
          </div>
          <div className="plan-row">
            <span className="plan-row__label">Status</span>
            <span className="plan-row__value">{plan.membershipStatus || '—'}</span>
          </div>
          {plan.membershipBegins && (
            <div className="plan-row">
              <span className="plan-row__label">Member Since</span>
              <span className="plan-row__value">{fmtPlanDate(plan.membershipBegins)}</span>
            </div>
          )}
          {plan.billingAmount != null && (
            <div className="plan-row">
              <span className="plan-row__label">Amount</span>
              <span className="plan-row__value">${plan.billingAmount.toFixed(2)}/mo</span>
            </div>
          )}
          {plan.nextBillDate && (
            <div className="plan-row">
              <span className="plan-row__label">Next Bill Date</span>
              <span className="plan-row__value">{fmtPlanDate(plan.nextBillDate)}</span>
            </div>
          )}
          <div className="plan-row">
            <span className="plan-row__label">Check-ins This Week</span>
            <span className="plan-row__value">{plan.checkInsThisWeek}</span>
          </div>
          {plan.lastCheckIn && (
            <div className="plan-row">
              <span className="plan-row__label">Last Visit</span>
              <span className="plan-row__value">{fmtPlanDate(plan.lastCheckIn)}</span>
            </div>
          )}
        </div>
      )}
    </Sheet>
  );
}

// ── Main Settings page ─────────────────────────────────────────────────────
type SheetType = 'password' | 'payment' | 'plan' | null;

export default function SettingsPage() {
  const [sheet, setSheet] = useState<SheetType>(null);

  return (
    <div className="page">
      <header className="page-header">
        <h1 className="page-header__title">Settings</h1>
      </header>
      <div className="page-content">
        <section className="settings-section">
          <h2 className="settings-section__title">Account</h2>
          <div className="settings-menu">
            <button className="settings-row" type="button" onClick={() => setSheet('password')}>
              <span className="settings-row__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={20} height={20}>
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </span>
              <span className="settings-row__label">Change Password</span>
              <span className="settings-row__chevron"><Chevron /></span>
            </button>
            <button className="settings-row" type="button" onClick={() => setSheet('payment')}>
              <span className="settings-row__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={20} height={20}>
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
              </span>
              <span className="settings-row__label">Update Payment Method</span>
              <span className="settings-row__chevron"><Chevron /></span>
            </button>
            <button className="settings-row" type="button" onClick={() => setSheet('plan')}>
              <span className="settings-row__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={20} height={20}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </span>
              <span className="settings-row__label">My Plan</span>
              <span className="settings-row__chevron"><Chevron /></span>
            </button>
          </div>
        </section>

        {isIosSafari() && !isInstalled() && (
          <section className="settings-section">
            <h2 className="settings-section__title">Add to Home Screen</h2>
            <div className="settings-menu">
              <div className="settings-install-card">
                <p className="settings-install-step">
                  <span className="settings-install-num">1</span>
                  Click the three dots
                </p>
                <p className="settings-install-step">
                  <span className="settings-install-num">2</span>
                  Tap the <strong>Share</strong> button at the bottom of Safari
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={18} height={18} style={{ verticalAlign: 'middle', marginLeft: 4 }}>
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                </p>
                <p className="settings-install-step">
                  <span className="settings-install-num">3</span>
                  Scroll down and tap <strong>"Add to Home Screen"</strong>
                </p>
                <p className="settings-install-step">
                  <span className="settings-install-num">4</span>
                  Tap <strong>"Add"</strong> in the top right corner
                </p>
              </div>
            </div>
          </section>
        )}
      </div>

      {sheet === 'password' && <PasswordSheet onClose={() => setSheet(null)} />}
      {sheet === 'payment'  && <PaymentSheet  onClose={() => setSheet(null)} />}
      {sheet === 'plan'     && <PlanSheet     onClose={() => setSheet(null)} />}
    </div>
  );
}
